import React from 'react';
import {App, Button, message as antdMessage, Skeleton, Upload} from 'antd';
import {FileExcelOutlined, FileOutlined, FilePdfOutlined, FileWordOutlined, FileZipOutlined, UploadOutlined} from '@ant-design/icons';
import {UploadFile} from 'antd/lib';
import {Attachment} from '../../hooks/useAttachments';
import API from '../../modules/settings/APIs';
import {API_PARAMS} from '../../constants/constants';
import {useTranslation} from '../../hooks/useTranslation';
import {useAppSelector} from '../../hooks/hooks';
import styles from './Attachments.module.css';

interface AttachmentsProps {
  id?: string;// id of component in DOM
  idEntity?: string;// entity refers id of risk, issue, event, etc.
  attachments?: UploadFile<Attachment>[];
  setAttachments: React.Dispatch<React.SetStateAction<UploadFile<Attachment>[] | undefined>>;
  isLoading: boolean;
}

const Attachments: React.FC<AttachmentsProps> = ({
                                                   id,
                                                   idEntity,
                                                   attachments,
                                                   setAttachments,
                                                   isLoading
                                                 }) => {
  const {modal} = App.useApp();
  const {t} = useTranslation();
  const {token} = useAppSelector((state) => state.session);
  const [messageApi, contextHolder] = antdMessage.useMessage();


  const getFileIcon = (file: UploadFile<Attachment>) => {
    const fileType = file.type || '';
    const fileName = file.name || '';


    if (fileType.includes('image')) {
      return null;
    }

    if (
      fileName.endsWith('.zip') ||
      fileName.endsWith('.rar') ||
      fileName.endsWith('.jar') ||
      fileName.endsWith('.tar') ||
      fileName.endsWith('.gz') ||
      fileName.endsWith('.7z') ||
      fileName.endsWith('.tar.gz') ||
      fileName.endsWith('.tgz') ||
      fileName.endsWith('.bz2') ||
      fileName.endsWith('.xz')
    ) {
      return <FileZipOutlined style={{fontSize: '16px', color: '#FFA500'}}/>;
    }

    if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
      return <FileExcelOutlined style={{fontSize: '16px', color: '#1D6F42'}}/>;
    }

    if (fileName.endsWith('.docx') || fileName.endsWith('.doc')) {
      return <FileWordOutlined style={{fontSize: '16px', color: '#2B579A'}}/>;
    }

    if (fileType.includes('pdf') || fileName.endsWith('.pdf')) {
      return <FilePdfOutlined style={{fontSize: '16px', color: '#FF0000'}}/>;
    }

    return <FileOutlined style={{fontSize: '16px', color: '#8c8c8c'}}/>;
  };


  const handleFileDeletion = async (file: UploadFile<Attachment>) => {
    const key = file.uid;

    if (key !== undefined) {
      try {
        const response = await fetch(`${API.issues.file.delete}?key=${key}`, {
          method: 'DELETE',
          headers: {
            'Authorization': 'Bearer ' + token,
            'Content-Type': 'application/json'
          }
        });
        const rep = await response.json();
        messageApi.open({
          type: rep.success ? 'success' : 'error',
          content: rep.message || 'File deleted successfully',
          duration: 4
        });

        return rep.success;
      } catch (e) {
        messageApi.error('Failed to delete file');
        return false;
      }
    }
    return Promise.resolve(true);
  };

  const handleFileRemove = (file: UploadFile<Attachment>) => {
    return new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('attachment.delete.title'),
        content: t('attachment.delete.content'),
        onOk: async () => {
          const success = await handleFileDeletion(file);
          if (success) {
            setAttachments((prev) => prev?.filter((item) => item.uid !== file.uid));
          }
          resolve(success);
        },
        onCancel: () => {
          resolve(false);
        },
        okText: t('attachment.delete.confirm'),
        cancelText: t('attachment.delete.cancel')
      });
    });
  };

  const handleFileChange = (info: any) => {
    let fileList = [...info.fileList];

    fileList = fileList.map((file) => {
      const data = file.response?.data;
      const key: string | undefined = typeof data === 'object' ? data?.key : data;
      const presignedUrl: string | undefined = typeof data === 'object' ? data?.presignedUrl : undefined;
      const uid = key || file.uid;

      if (!presignedUrl) {
        return {...file, uid};
      }

      const isImage = typeof file.type === 'string' && file.type.startsWith('image/');
      return {
        ...file,
        uid,
        url: presignedUrl,
        thumbUrl: isImage ? presignedUrl : file.thumbUrl,
      };
    });

    setAttachments(fileList);
  };

  if (isLoading) {
    return (
      <div style={{width: '100%'}}>
        {contextHolder}
        <Skeleton.Button
          active
          size="small"
          // style={{
          //   width: '100%',
          //   height: '40px',
          //   marginBottom: '16px'
          // }}
        />
        <Skeleton
          active
          // paragraph={{rows: 3, width: ['100%', '80%', '60%']}}
          title={false}
        />
      </div>
    );
  }

  return (
    <>
      {contextHolder}
      <div className={styles.attachmentsContainer}>
        <Upload<Attachment>
          style={{width: '100%'}}
          multiple={true}
          // listType="picture"
          iconRender={getFileIcon}
          fileList={attachments}
          action={`${API.issues.file.upload}?uid=${idEntity}&module=${API_PARAMS.IMPACT.ISSUES_ENTITY}&pathSubModule=&uuidSubEntity=`}
          headers={{
            Authorization: 'Bearer ' + token
          }}
          onRemove={handleFileRemove}
          onChange={handleFileChange}
        >
          <Button type="dashed" icon={<UploadOutlined/>} style={{width: '100%'}}>
            Upload
          </Button>
        </Upload>
      </div>
    </>
  );
};

export default Attachments;

